import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { callAgreeTerms } from "api/MuyeonApi";
import {
  CONSENT_ITEMS, isAllRequired, toAgreements,
  stashSignupAgreements, setConsentMode,
} from "util/consent";

const TERMS_DRAFT_KEY = "muyeon.signupTermsDraft";

const readTermsDraft = () => {
  try { return JSON.parse(sessionStorage.getItem(TERMS_DRAFT_KEY) || "{}"); }
  catch (e) { return {}; }
};

/**
 * 약관 동의 화면(웹 폴백) — 네이티브 미탑재(브라우저)일 때. 앱에서는 네이티브 동의화면이 대신 뜬다.
 *  mode=signup: 동의 → /register(동의값 캐리) / 미동의 → /login
 *  mode=gate  : 동의 → POST /auth/terms/agree 저장 후 홈 / 미동의 → 로그아웃 후 /login
 *  #join_terms 마크업·CSS 재활용(커스텀 CSS 없음).
 */
export default function SignupTerms() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, updateUser, logout } = useAuth();
  const mode = params.get("mode") === "gate" ? "gate" : "signup";
  const redirect = params.get("redirect") || "/home";
  const returnsToQuote = mode === "signup" && redirect.startsWith("/quoteRequest");

  const [checked, setChecked] = useState(readTermsDraft);
  const [busy, setBusy] = useState(false);

  const allChecked = useMemo(() => CONSENT_ITEMS.every((i) => !!checked[i.key]), [checked]);
  const allRequired = isAllRequired(checked);

  const handleCheckAll = (e) => {
    const v = e.target.checked;
    const next = {};
    CONSENT_ITEMS.forEach((i) => (next[i.key] = v));
    setChecked(next);
  };
  const toggle = (key) => (e) => setChecked((prev) => ({ ...prev, [key]: e.target.checked }));

  const goDetail = (doc) => {
    try { sessionStorage.setItem(TERMS_DRAFT_KEY, JSON.stringify(checked)); } catch (e) { /* noop */ }
    navigate(`/policy?doc=${doc}`);
  };

  const onCancel = () => {
    try { sessionStorage.removeItem(TERMS_DRAFT_KEY); } catch (e) { /* noop */ }
    if (mode === "gate") logout();
    if (returnsToQuote) {
      navigate(redirect, { replace: true });
      return;
    }
    navigate("/login", { replace: true });
  };

  const onNext = async () => {
    if (!allRequired || busy) return;
    const agreements = toAgreements(checked);
    try { sessionStorage.removeItem(TERMS_DRAFT_KEY); } catch (e) { /* noop */ }
    if (mode === "gate") {
      setBusy(true);
      try {
        await callAgreeTerms(agreements);
        updateUser({ termsAgreed: true, termsReconsent: false });
        navigate(redirect && redirect !== "/login" ? redirect : "/home", { replace: true });
      } catch (e) {
        setBusy(false);
      }
      return;
    }
    // signup: 동의값을 가입폼으로 캐리 후 회원가입 페이지로
    stashSignupAgreements(agreements);
    setConsentMode("signup");
    navigate(`/register?redirect=${encodeURIComponent(redirect)}`, returnsToQuote ? { replace: true } : undefined);
  };

  return (
    <div className="container">
      <div className="top-bar">
        <button type="button" className="ico-btn back" onClick={onCancel}>
          <i className="arrow" /><span className="visually-hidden">뒤로가기</span>
        </button>
        <h2 className="page-name">약관 동의</h2>
      </div>
      <div id="join_terms" className="page">
        <div className="content-wrap">
          {/* 관리자가 약관을 '재동의'로 개정하면 기존 회원도 이 화면으로 온다 — 왜 다시 동의하는지 알린다. */}
          <p className="page-tit">
            {mode === "gate" && user?.termsReconsent
              ? <>약관이 개정되었어요.<br />확인 후 다시 동의해 주세요.</>
              : <>무용연 이용을 위해<br />약관에 동의해 주세요.</>}
          </p>
          <div className="terms-wrap">
            <label htmlFor="check-all" className="checkbox-md">
              <input id="check-all" type="checkbox" checked={allChecked} onChange={handleCheckAll} />
              <span className="box" /><span className="label">약관 전체동의</span>
            </label>
            <ul className="check-list">
              {CONSENT_ITEMS.map((it) => {
                const id = `terms-${it.key}`;
                return (
                  <li key={it.key} className="list-item">
                    <label htmlFor={id} className="checkbox-sm">
                      <input id={id} type="checkbox" checked={!!checked[it.key]} onChange={toggle(it.key)} />
                      <span className="box" />
                      <span className="label">({it.required ? "필수" : "선택"}) {it.label}</span>
                    </label>
                    {it.doc && (
                      <button type="button" onClick={() => goDetail(it.doc)} className="btn-sm">
                        <i className="arrow" /><span className="visually-hidden">약관보기</span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
      <div className="tab-bar-btn-group fixed">
        <div className="btn-group">
          <button type="button" className="btn-md filled primary" disabled={!allRequired || busy} onClick={onNext}>
            동의하고 계속
          </button>
        </div>
      </div>
    </div>
  );
}
